// Second step of sign-in when a user has two-factor authentication on (T04, T12 §9.1).
// Both `features/auth/client.ts` (twoFactorClient twoFactorPage) and `login.tsx` already send the
// user here, so the route has to exist; without it TanStack Router rejects the navigation. (D-035)
import { useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@vbltc/ui/button";
import { Input } from "@vbltc/ui/input";
import { AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, AUTH_LABEL_CLASS, AUTH_LINK_CLASS } from "@vbltc/ui";
import { AuthPage } from "../app/auth-page";
import { authClient } from "../features/auth/client";
import { formString } from "../features/auth/form-value";
import { clearUserState } from "../features/auth/clear-user-state";
import { returnTarget, signedInUserId } from "../features/auth/return-to";
import { isServerPage } from "../features/auth/session-read";
import { log } from "../telemetry";
import { twoFactorSearchSchema } from "@/lib/route-search";

export const Route = createFileRoute("/two-factor")({
  validateSearch: twoFactorSearchSchema,
  component: TwoFactorPage,
});

function TwoFactorPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const code = formString(new FormData(e.currentTarget), "code").trim();
    const { data, error: err } = useBackupCode
      ? await authClient.twoFactor.verifyBackupCode({ code })
      : await authClient.twoFactor.verifyTotp({ code });

    setPending(false);
    if (err) {
      // Status only. The code is a credential and never reaches a log (T12 rule 1).
      log.debug("two-factor rejected", err.status);
      setError("That code is not valid. Try again.");
      return;
    }
    // "/" lands on the first ACO with data (CR-005 item 4, J-191); a `//host` target too (J-210); and
    // J-243: the page to return to only for the user who left it, with nothing cached from another.
    const target = returnTarget(redirect, signedInUserId(data));
    clearUserState(queryClient, router);
    void navigate({ href: target, reloadDocument: isServerPage(target) });
  }

  return (
    // The same page as sign-in (J-242, the owner's approved design): one card, one primary action.
    <AuthPage
      heading="Two-factor authentication"
      description={
        useBackupCode
          ? "Enter one of your backup codes."
          : "Enter the 6-digit code from your authenticator app."
      }
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        aria-label="Two-factor authentication"
        className="flex flex-col gap-4.5"
      >
        <label className={AUTH_LABEL_CLASS}>
          {useBackupCode ? "Backup code" : "Authentication code"}
          <Input
            name="code"
            type="text"
            inputMode={useBackupCode ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoFocus
            required
            className={AUTH_INPUT_CLASS}
          />
        </label>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={pending}
          data-telemetry="two-factor-submit"
          className={AUTH_BUTTON_CLASS}
        >
          Verify
        </Button>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            setUseBackupCode((v) => !v);
            setError(null);
          }}
          data-telemetry="two-factor-toggle-method"
          className={AUTH_LINK_CLASS}
        >
          {useBackupCode ? "Use my authenticator app instead" : "Use a backup code instead"}
        </Button>
      </form>
    </AuthPage>
  );
}
